import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authenticate } from "../../services/user";
import { Snackbar } from "../../components/snackbar";
import {
  DEFAULT_SNACK,
  EMPTY_CREDENTIALS,
  ERROR_CREDENTIALS,
  SUCCESS_CREDENTIALS,
} from "../../utils/snacks";
import { LOGIN_USER } from "../../utils/states";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import LoginIcon from "@mui/icons-material/Login";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { Spinner } from "../../components/spinner";
import "./index.css";

export const Login = () => {
  const [credentials, setCredentials] = useState(LOGIN_USER);
  const [snack, setSnackbar] = useState(DEFAULT_SNACK);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const resetSnackbar = () => {
    setTimeout(() => {
      setSnackbar(DEFAULT_SNACK);
    }, 3000);
  };

  const handleChange = ({ target: { name, value } }) => {
    setCredentials({
      ...credentials,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!credentials.email || !credentials.password) {
      setSnackbar(EMPTY_CREDENTIALS);
      resetSnackbar();
      return;
    }

    // Validar formato de email básico
    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // if (!emailRegex.test(credentials.email)) {
    //   setSnackbar({
    //     severity: "error",
    //     message: "Formato de email inválido",
    //     visible: true,
    //   });
    //   resetSnackbar();
    //   return;
    // }

    setLoading(true);

    try {
      const response = await authenticate({
        email: credentials.email.trim().toLowerCase(),
        password: credentials.password,
      });

      if (response.err || response.error) {
        setSnackbar({
          severity: "error",
          message: response.err || response.error || "Credenciales inválidas",
          visible: true,
        });
        resetSnackbar();
        setLoading(false);
        return;
      }

      if (response.token && response.user) {
        setSnackbar(SUCCESS_CREDENTIALS);
        resetSnackbar();

        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        throw new Error("Respuesta inválida del servidor");
      }
    } catch (err) {
      console.error("Error en login:", err);
      setSnackbar({
        severity: "error",
        message: "Error de conexión. Verifica tus credenciales e intenta nuevamente.",
        visible: true,
      });
      resetSnackbar();
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
      {loading && <Spinner />}

      <div className="w-full max-w-md">
        <div className="bg-sidebar-bg rounded-2xl shadow-2xl border border-sidebar-accent overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-4">
              <DirectionsCarIcon className="text-white" sx={{ fontSize: 32 }} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">GaragePro</h1>
            <p className="text-blue-100 text-sm">Sistema de Gestión Automotriz</p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <EmailIcon className="text-gray-500" sx={{ fontSize: 20 }} />
                </div>
                <input
                  type="text"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  placeholder="usuario@ejemplo.com"
                  className="w-full pl-11 pr-4 py-3 bg-black border border-sidebar-accent rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <LockIcon className="text-gray-500" sx={{ fontSize: 20 }} />
                </div>
                <input
                  type="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3 bg-black border border-sidebar-accent rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20 flex items-center justify-center gap-2 group"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <LoginIcon className="group-hover:scale-110 transition-transform" />
                  Iniciar Sesión
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                disabled={loading}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>

          <div className="bg-sidebar-accent/30 px-8 py-4 text-center border-t border-sidebar-accent">
            <p className="text-xs text-gray-500">
              © 2024 GaragePro. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>

      <Snackbar
        severity={snack.severity}
        message={snack.message}
        visible={snack.visible}
      />
    </div>
  );
};
